import { useMemo, useState } from 'react'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getUserBooksLockedForUserYear } from '@/reducers/auth/user.selectors'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import {
  CREATE_MULTIPLE_MANUAL_TRANSACTIONS_KEY,
  createMultipleManualTransactions,
} from '@/features/transactions/transactions.slice'
import { ManualTransactionsCard } from './ManualTransactionsCard'
import {
  EMPTY_ROW,
  areRowsValid,
  toDateBounds,
  toManualTransactions,
  toManualTransactionsOutcome,
  toSaveErrorMessage,
  type ManualTransactionRow,
} from './manualTransactions'
import type { WidgetProps } from './types'

export const ManualTransactionsWidget = ({
  resolved,
  onOutcome,
}: WidgetProps) => {
  const dispatch = useAppDispatch()
  const booksLockedForUserYear = useReselector(getUserBooksLockedForUserYear)
  const bounds = useMemo(
    () => toDateBounds(booksLockedForUserYear),
    [booksLockedForUserYear]
  )
  const saving = useReselector(
    getIsFetching,
    CREATE_MULTIPLE_MANUAL_TRANSACTIONS_KEY
  )
  const saveError = useReselector(
    getFetchError,
    CREATE_MULTIPLE_MANUAL_TRANSACTIONS_KEY
  )
  const [rows, setRows] = useState<ManualTransactionRow[]>([EMPTY_ROW])
  // Once the rows are written, a refused outcome reopens the card; Save must not write them twice.
  const [saved, setSaved] = useState(false)
  // The fetch key is shared with the spreadsheet upload, so only this card's own attempt shows its error.
  const [attempted, setAttempted] = useState(false)

  if (resolved) return null

  const save = async () => {
    if (!saved) {
      setAttempted(true)
      try {
        await dispatch(
          createMultipleManualTransactions(toManualTransactions(rows))
        )
      } catch {
        return
      }
      setSaved(true)
    }
    const { text, display } = toManualTransactionsOutcome(rows)
    onOutcome(text, display)
  }

  return (
    <ManualTransactionsCard
      rows={rows}
      bounds={bounds}
      canSave={areRowsValid(rows, bounds)}
      saving={saving}
      locked={saving || saved}
      saveError={attempted && saveError ? toSaveErrorMessage(saveError) : null}
      onChange={(index, row) =>
        setRows((current) =>
          current.map((existing, i) => (i === index ? row : existing))
        )
      }
      onAddRow={() => setRows((current) => [...current, EMPTY_ROW])}
      onSave={() => void save()}
    />
  )
}
